/**
 * usePipelineStagePersistence - debounce/retry/guard machinery for pipeline
 * stage editors (CreatePipelineRenderer, PipelineCustomizationRenderer).
 *
 * Delegates coordination, registration, freeze gating, change detection, and
 * debounce to `useCoordinatedAutoSave`. Retains only the two pipeline-specific
 * behaviours the shared stack lacks:
 *
 *   1. **Retry-with-backoff.** Exponential back-off up to MAX_RETRIES before
 *      surfacing `apiError`; invocations are serialised so overlapping edits
 *      never race, and `retryNow` re-sends the value still held pending.
 *   2. **Sync-effect guard.** `isGuardActive` is true while a local edit has
 *      not yet been acknowledged by the server; callers gate adoption of any
 *      incoming server value on it.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import { useCoordinatedAutoSave } from '@/components/proposals/shared/useCoordinatedAutoSave';
import { logger } from '@/lib/logger';

const MAX_RETRIES = 3;

interface UsePipelineStagePersistenceOptions<T> {
  /** Delay after the last edit before a persist attempt is scheduled. */
  debounceMs: number;
  /** Shown as `apiError` once MAX_RETRIES is exhausted. */
  errorMessage: string;
  /** Extra fields merged into the retry-exhaustion log call (e.g. proposalId, actionId). */
  logContext?: Record<string, unknown>;
  /**
   * Registers this editor with the approval flush rail
   * (`AutoSaveCoordinationProvider`) so `flushAllAndWait()` can see and flush a
   * pending stage save before the approve POST. Must be stable for the lifetime
   * of the editor and unique per action card (e.g. `create-pipeline-42`).
   * Omitting it leaves the hook uncoordinated but still fully functional.
   */
  coordinationId?: string;
  /**
   * The value already persisted server-side when the editor mounts. Seeds
   * change detection so reverting an edit to this value issues no write.
   */
  initialSavedValue?: T;
}

export interface UsePipelineStagePersistenceResult<T> {
  /** Register a local edit: arms a debounced persist. */
  notifyEdit: (data: T) => void;
  /** Flush a pending debounced persist immediately (e.g. on blur). Fire-and-forget. */
  flushPersist: () => void;
  /** Set once MAX_RETRIES is exhausted; cleared on the next successful save. */
  apiError: string | null;
  /**
   * True while a local edit is pending or in-flight. Callers must not adopt
   * an incoming server payload while this is true — doing so would rewind
   * in-progress edits to a stale server value.
   */
  isGuardActive: boolean;
  /** Re-attempt the pending value the hook still holds after a failed save. */
  retryNow: () => void;
  /**
   * Advance the change-detection baseline to `value` without writing. Call
   * from the sync effect when it adopts a server value that did not originate
   * from a local save, so a later revert to that value is correctly treated as
   * a no-op instead of issuing a spurious write.
   */
  syncSavedValue: (data: T) => void;
}

export function usePipelineStagePersistence<T>(
  persistFn: (data: T) => Promise<void>,
  options: UsePipelineStagePersistenceOptions<T>
): UsePipelineStagePersistenceResult<T> {
  const { debounceMs, coordinationId, initialSavedValue } = options;

  const persistFnRef = useRef(persistFn);
  persistFnRef.current = persistFn;
  const logContextRef = useRef(options.logContext);
  logContextRef.current = options.logContext;
  const errorMessageRef = useRef(options.errorMessage);
  errorMessageRef.current = options.errorMessage;

  // Stable synthetic id used when no coordinationId is provided — registration
  // is harmless and allows the hook to be used purely for debounce/change-detection.
  const stableIdRef = useRef(
    `pipeline-stage-${Math.random().toString(36).slice(2)}`
  );

  const [apiError, setApiError] = useState<string | null>(null);

  // One save attempt with exponential-backoff retry up to MAX_RETRIES. Rejects
  // once retries are exhausted so the coordination flush can reject and block
  // approval.
  const attemptWithRetry = useCallback(async (data: T): Promise<void> => {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        await persistFnRef.current(data);
        setApiError(null);
        return;
      } catch (error) {
        if (attempt < MAX_RETRIES) {
          const delay = Math.pow(2, attempt) * 1000;
          await new Promise<void>(r => setTimeout(r, delay));
          continue;
        }
        // Retries exhausted — surface the error and re-throw so
        // useCoordinatedAutoSave marks the write errored and flush() rejects.
        setApiError(errorMessageRef.current);
        logger.error(
          'Max retries exhausted for pipeline stage persistence',
          error instanceof Error ? error : undefined,
          { ...logContextRef.current, maxRetries: MAX_RETRIES }
        );
        throw error;
      }
    }
  }, []);

  // Tail of the serialised save chain. `useCoordinatedAutoSave` fires each
  // debounced save immediately without awaiting the previous one, so a second
  // edit made while the first is still backing off would otherwise run two
  // retry chains concurrently — the older, slower one could echo its stale
  // payload after the newer edit landed (and both would race the actionId
  // churn). Chaining every invocation onto the prior one preserves save order
  // and keeps the flush barrier meaningful.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());

  // Wrapped save passed to useCoordinatedAutoSave: serialises attempts so they
  // run strictly in the order they were armed.
  const wrappedSave = useCallback(
    (data: T): Promise<void> => {
      // Run after the previous invocation settles — proceed even if it failed
      // (a newer edit must still be attempted).
      const run = saveChainRef.current.then(
        () => attemptWithRetry(data),
        () => attemptWithRetry(data)
      );
      // The chain tail never rejects, so the next invocation always links on
      // cleanly; the real (possibly rejecting) promise is returned to the hook.
      saveChainRef.current = run.then(
        () => undefined,
        () => undefined
      );
      return run;
    },
    [attemptWithRetry]
  );

  const { save, flush, isGuardActive, syncSavedBaseline } =
    useCoordinatedAutoSave<T>({
      registrationId: coordinationId ?? stableIdRef.current,
      onSave: wrappedSave,
      initialSavedValue,
      debounceMs,
      enabled: true,
    });

  const notifyEdit = useCallback((data: T) => save(data), [save]);

  // Preserve the sync fire-and-forget signature callers rely on (blur/unmount).
  // Rejection is already surfaced via apiError.
  const flushPersist = useCallback(() => {
    void flush().catch(() => {});
  }, [flush]);

  // Clears the error and flushes the value the hook still holds pending. A
  // failed save leaves that value pending and does not advance the last-saved
  // baseline, so the flush re-sends it — and if the user typed again after the
  // failure, the pending value is that NEWER edit, so retry never resurrects
  // the stale failed snapshot over it.
  const retryNow = useCallback(() => {
    setApiError(null);
    void flush().catch(() => {});
  }, [flush]);

  return useMemo(
    () => ({
      notifyEdit,
      flushPersist,
      apiError,
      isGuardActive,
      retryNow,
      syncSavedValue: syncSavedBaseline,
    }),
    [
      notifyEdit,
      flushPersist,
      apiError,
      isGuardActive,
      retryNow,
      syncSavedBaseline,
    ]
  );
}
